import React, { useState, useEffect, useMemo, useCallback } from 'react';
import CategoryFilterBar from './CategoryFilterBar';
import { useParams, useSearchParams } from 'react-router-dom';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import PresentationItem from './PresentationItem';
import PosProductCard from './PosProductCard';
import ProductGrid from './ProductGrid';
import NewVarietyModal from '../components/NewVarietyModal';
import ProductCreationModal from "../components/ProductCreationModal";
import ProductFormDialog from './ProductFormDialog';
import ProductSearchInput from './ProductSearchInput';
import VarietyGroupManager from './VarietyGroupManager';
import VarietyGroupTabs from './VarietyGroupTabs';
import { getProductPresentations, deleteProduct, updateProduct, deleteVariety, uploadFile, getCategories, getBranchTaxes } from '../api';
import InlineLoading from './InlineLoading';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useProductEvents from '../hooks/useProductEvents';
import useProductSearchSettings from '../hooks/useProductSearchSettings';
import { applyProductEvent } from '../utils/productEventPatch';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import { ALL_VARIETY_GROUP_ID, filterVarietiesByGroup } from '../utils/varietyGroups';
import { getProductSearchMatch, tokenizeProductSearchQuery } from '../utils/productSearch';
import { reconcileProductUpdate } from '../utils/productUpdateReconciliation';

export const resolveProductCategoryId = (product) =>
  product?.categoryId ?? product?.Category?.id ?? null;

export const resolveProductSubcategoryId = (product) =>
  product?.subcategoryId ?? product?.Subcategory?.id ?? null;

export const prepareCategoriesForDisplay = (categories) => categories
  .slice()
  .sort((a, b) => String(a.name).localeCompare(String(b.name), 'es', { sensitivity: 'base' }))
  .map((category) => ({
    ...category,
    label: category.isActive === false ? `${category.name} (inactiva)` : category.name,
    CategorySubcategories: (category.CategorySubcategories || [])
      .slice()
      .sort((a, b) =>
        Number(a.sortOrder || 0) - Number(b.sortOrder || 0)
        || String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' })
      ),
  }));

export const buildStockItemByVarietyId = (stockItems) => {
  const map = new Map();
  stockItems.forEach((item) => {
    if (item?.varietyId !== null && item?.varietyId !== undefined) {
      map.set(Number(item.varietyId), item);
    }
  });
  return map;
};

export const buildMissingUpdateVarietyIds = (varietiesMissingUpdate) => new Set(
  varietiesMissingUpdate
    .map((item) => Number(item?.varietyId))
    .filter((id) => Number.isInteger(id))
);

export const resolveProductCategoryName = (product, categories) => {
  const resolvedId = resolveProductCategoryId(product);
  const category = categories.find((entry) => String(entry.id) === String(resolvedId));
  return category?.name ?? product?.Category?.name ?? 'Sin categoria';
};

export const resolveProductSubcategoryName = (product, categories) => {
  const resolvedSubcategoryId = resolveProductSubcategoryId(product);
  const resolvedCategoryId = resolveProductCategoryId(product);
  const category = categories.find((entry) => String(entry.id) === String(resolvedCategoryId));
  const subcategory = (category?.CategorySubcategories || []).find(
    (entry) => String(entry.id) === String(resolvedSubcategoryId)
  );
  return subcategory?.name ?? product?.Subcategory?.name ?? 'Sin subcategoria';
};

const ProductList = ({
  branchId: branchIdProp,
  embedded = false,
  canReadProducts = true,
  canReadPrices: canReadPricesProp,
  canCreateProduct = true,
  canUpdateProduct = true,
  canDeleteProduct = true,
}) => {
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
	  const [products, setProducts] = useState([]);
	  const [search, setSearch] = useState('');
	  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [taxesLoading, setTaxesLoading] = useState(false);
  const [taxesError, setTaxesError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
	const [showAddVarietyModal, setShowAddVarietyModal] = useState(false);
  const [activeVarietyGroupId, setActiveVarietyGroupId] = useState(ALL_VARIETY_GROUP_ID);
	const params = useParams();
	const [searchParams] = useSearchParams();
	const branchId = branchIdProp ?? params.branchId ?? localStorage.getItem('selectedBranchId');
	const targetFormatId = searchParams.get('varietyPriceId');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [searchVarietyResults, setSearchVarietyResults] = useState([]);
	const [suggestedSearchVarietyResults, setSuggestedSearchVarietyResults] = useState([]);
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const { searchMode } = useProductSearchSettings();
  const canReadTaxes = hasPermission(user, 'tax.read');
  const canReadPrices = typeof canReadPricesProp === 'boolean'
    ? canReadPricesProp
    : hasPermission(user, 'price.read');
  const selectedProduct = useMemo(() => {
    if (selectedProductId === null || selectedProductId === undefined) return null;
    return products.find((product) => String(product.id) === String(selectedProductId)) || null;
  }, [products, selectedProductId]);
  const applyCatalogEvent = useCallback((event) => {
    setProducts((prev) => {
      const nextProducts = applyProductEvent(prev, event);
      return nextProducts || prev;
    });
  }, []);

  useProductEvents(branchId, applyCatalogEvent);

  const closeSelectedProduct = useCallback(() => {
    setSelectedProductId(null);
    setShowEdit(false);
    setShowAddVarietyModal(false);
  }, []);

  useEffect(() => {
    if (selectedProductId === null || selectedProductId === undefined || selectedProduct) return;
    setShowEdit(false);
    setShowAddVarietyModal(false);
  }, [selectedProduct, selectedProductId]);

  useEffect(() => {
    if (!targetFormatId || !products.length) return;
    const owner = products.find((product) => (product.Varieties || []).some(
      (variety) => (variety.VarietyPrices || []).some(
        (format) => String(format.id) === String(targetFormatId)
      )
    ));
    if (owner) setSelectedProductId(owner.id);
  }, [products, targetFormatId]);

  useEffect(() => {
    setActiveVarietyGroupId(ALL_VARIETY_GROUP_ID);
  }, [selectedProduct?.id]);

  useEffect(() => {
    if (!selectedProduct) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedProduct]);

  useEffect(() => {
    if (!selectedProduct || showEdit || showAddVarietyModal) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      const openModalDialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      if (openModalDialogs.length > 1) return;
      closeSelectedProduct();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeSelectedProduct, selectedProduct, showAddVarietyModal, showEdit]);

  const selectedProductVarieties = useMemo(() => {
    const varieties = Array.isArray(selectedProduct?.Varieties) ? selectedProduct.Varieties : [];
    return filterVarietiesByGroup(
      varieties,
      selectedProduct?.VarietyGroups,
      activeVarietyGroupId
    );
  }, [selectedProduct?.Varieties, selectedProduct?.VarietyGroups, activeVarietyGroupId]);

  const activeVarietyGroup = selectedProduct?.VarietyGroups?.find(
    (group) => String(group.id) === String(activeVarietyGroupId)
  );
  const openAddVariety = (groupId = activeVarietyGroupId) => {
    setActiveVarietyGroupId(groupId);
    setShowAddVarietyModal(true);
  };

  const categoriesForDisplay = useMemo(
    () => prepareCategoriesForDisplay(categories),
    [categories]
  );
  const categoriesToRender = useMemo(() => {
    if (selectedCategory === 'all' || search) return categoriesForDisplay;
    return categoriesForDisplay.filter(
      (category) => String(category.id) === String(selectedCategory)
    );
  }, [categoriesForDisplay, search, selectedCategory]);

	  const loadProducts = useCallback(async () => {
	    setLoading(true);
	    setError(null);
	    try {
	      const data = await getProductPresentations(branchId);
	      setProducts(Array.isArray(data?.data) ? data.data : []);
	    } catch (err) {
	      console.error('Error fetching product presentations:', err);
	      setError('No se pudieron cargar los productos.');
	    } finally {
	      setLoading(false);
	    }
	  }, [branchId, canReadProducts]);
	  useEffect(() => {
	    if (!canReadProducts) {
	      setLoading(false);
	      setError('No tienes permisos para ver productos.');
	      return;
	    }
      if (!branchId) {
        setLoading(false);
        setError('Selecciona una sucursal para ver productos.');
        return;
      }
      setProducts([]);
      closeSelectedProduct();
	    loadProducts();
	  }, [branchId, canReadProducts, closeSelectedProduct, loadProducts]);

  useEffect(() => {
    if (!branchId) return;
    setCategoriesLoading(true);
    setCategoriesError(null);
    getCategories({ includeAvailability: true, branchId })
      .then((data) => {
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Error fetching categories:', err);
        setCategories([]);
        setCategoriesError('No se pudieron cargar las categorías.');
      })
      .finally(() => setCategoriesLoading(false));
  }, [branchId]);

  useEffect(() => {
    if (!branchId) return;
    if (!canReadTaxes) {
      setTaxes([]);
      setTaxesError('No tienes permisos para ver impuestos.');
      return;
    }
    setTaxesLoading(true);
    setTaxesError(null);
    getBranchTaxes(branchId)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        const active = list.filter((tax) => tax.branchActive && tax.isActive !== false);
        setTaxes(active);
      })
      .catch((err) => {
        console.error('Error fetching taxes:', err);
        setTaxes([]);
        setTaxesError('No se pudieron cargar los impuestos.');
      })
      .finally(() => setTaxesLoading(false));
  }, [branchId, canReadTaxes]);

  useEffect(() => {
    if (selectedCategory === 'all') return;
    const isStillValid = categoriesForDisplay.some(
      (category) => String(category.id) === String(selectedCategory)
    );
    if (!isStillValid) {
      setSelectedCategory('all');
    }
  }, [categoriesForDisplay, selectedCategory]);

  const handleProductCreated = useCallback(async (product) => {
    if (!product?.id) return;
    applyCatalogEvent({
      action: 'create-product',
      productId: product.id,
      product,
    });
    await loadProducts();
    setSelectedProductId(product.id);
  }, [applyCatalogEvent, loadProducts]);

  const handleUpdateProduct = async (values) => {
    if (!canUpdateProduct) return;
    if (!selectedProduct?.id) throw new Error('No se encontro el producto seleccionado.');
    const {
      productImageFile,
      removeProductImage,
      ...data
    } = values;
    if (productImageFile) {
      const uploaded = await uploadFile(productImageFile);
      if (!uploaded?.url) throw new Error('No se pudo subir la imagen del producto.');
      data.productImage = uploaded.url;
    } else if (removeProductImage) {
      data.productImage = null;
    }
    const updated = await updateProduct(branchId, selectedProduct.id, data);
    await reconcileProductUpdate({
      updatedProduct: updated,
      fallbackProduct: data,
      productId: selectedProduct.id,
      applyCatalogEvent,
      refreshProducts: loadProducts,
    });
    setShowEdit(false);
  };

  const handleDeleteProduct = async (id) => {
    try {
      const approved = await confirm({
        title: 'Eliminar producto',
        message: '¿Estás seguro de que deseas eliminar este producto?',
        confirmLabel: 'Eliminar',
        cancelLabel: 'Cancelar',
        tone: 'danger',
      });
      if (!approved) return;
      setIsLoading(true);
      await deleteProduct(branchId, id);
      applyCatalogEvent({
        action: 'delete-product',
        productId: id,
      });
      closeSelectedProduct();
    } catch (error) {
      console.error('Error deleting product:', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddVariety = async (variety) => {
    if (!canUpdateProduct || !selectedProductId || !variety) return;
    applyCatalogEvent({
      action: 'add-variety',
      productId: selectedProductId,
      varietyId: variety.id,
      variety,
      ...(Array.isArray(variety.groups) ? { varietyGroups: variety.groups } : {}),
    });
    setActiveVarietyGroupId(variety.groupId != null ? String(variety.groupId) : ALL_VARIETY_GROUP_ID);
  };

  const handleVarietySaved = useCallback((variety) => {
    if (!variety?.id) return;
    applyCatalogEvent({
      action: 'update-variety',
      varietyId: variety.id,
      variety,
    });
  }, [applyCatalogEvent]);

	  const filterProducts = (searchValue, category) => {
	    let filtered = products;
	    let varietyResults = [];
	    let suggestedVarietyResults = [];
	    const searchTokens = tokenizeProductSearchQuery(searchValue);
	
	    if (searchTokens.length) {
        const seenVarietyKeys = new Set();
	      const searchEntries = products
	        .map((product) => ({
	          product,
	          match: getProductSearchMatch(product, searchTokens, { searchMode }),
	        }))
	        .filter(({ match }) => match.matches);

        const exactEntries = searchEntries.filter(({ match }) => match.exactMatches);
        const suggestedEntries = searchEntries.filter(({ match }) =>
          !match.exactMatches && match.suggestedMatches
        );

	      filtered = exactEntries.map(({ product }) => product);
	      varietyResults = exactEntries.flatMap(({ product, match }) =>
	        match.matchingVarieties
            .map((variety) => ({ product, variety }))
            .filter((entry) => {
              const key = `${entry.product.id}-${entry.variety.id}`;
              if (seenVarietyKeys.has(key)) return false;
              seenVarietyKeys.add(key);
              return true;
            })
	      );
        suggestedVarietyResults = suggestedEntries.flatMap(({ product, match }) =>
          match.suggestedMatchingVarieties
            .map((variety) => ({ product, variety }))
            .filter((entry) => {
              const key = `${entry.product.id}-${entry.variety.id}`;
              if (seenVarietyKeys.has(key)) return false;
              seenVarietyKeys.add(key);
              return true;
            })
        );
	    } else if (category !== 'all') {
	      filtered = products.filter(
          (product) => String(resolveProductCategoryId(product)) === String(category)
        );
	    }
	
	    setFilteredProducts(filtered);
	    setSearchVarietyResults(varietyResults);
	    setSuggestedSearchVarietyResults(suggestedVarietyResults);
	  };

	  useEffect(() => {
	    filterProducts(search, selectedCategory);
	  }, [search, selectedCategory, products, searchMode]);

  const handleDeleteVariety = async (id) => {
    if (!canUpdateProduct) return;
    const approved = await confirm({
      title: 'Eliminar variedad',
      message: '¿Estás seguro de que deseas eliminar esta variedad y todos sus formatos en todas las sucursales?',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    await deleteVariety(branchId, id);
    applyCatalogEvent({
      action: 'delete-variety',
      varietyId: id,
    });
  };

  if (loading) {
    return (
      <p>
        <InlineLoading />
      </p>
    );
  }
  if (error) return <p>{error}</p>;

  return (
    <>
      {isModalOpen && (
        <ProductCreationModal
          onClose={setIsModalOpen}
          onProductCreated={handleProductCreated}
          branchId={branchId}
          canCreateProduct={canCreateProduct}
          categories={categoriesForDisplay}
          categoriesLoading={categoriesLoading}
          categoriesError={categoriesError}
          taxes={taxes}
          taxesLoading={taxesLoading}
          taxesError={taxesError}
          canReadTaxes={canReadTaxes}
          canUpdateProduct={canUpdateProduct}
        />
      )}
      {selectedProduct && showEdit && (
        <ProductFormDialog
          mode="edit"
          initialProduct={selectedProduct}
          categories={categoriesForDisplay}
          categoriesLoading={categoriesLoading}
          categoriesError={categoriesError}
          taxes={taxes}
          taxesLoading={taxesLoading}
          taxesError={taxesError}
          canReadTaxes={canReadTaxes}
          canSave={canUpdateProduct}
          onClose={() => setShowEdit(false)}
          onSubmit={handleUpdateProduct}
        />
      )}
      <NewVarietyModal
        isOpen={showAddVarietyModal}
        branchId={branchId}
        initialGroupId={activeVarietyGroupId}
        product={selectedProduct}
        existingVarieties={selectedProduct?.Varieties || []}
        barcodeScope={selectedProduct?.barcodeScope}
        onClose={() => { setShowAddVarietyModal(false); }}
        onVarietyCreated={handleAddVariety}
        canUpdateProduct={canUpdateProduct}
      />

      <div className={embedded ? "" : "p-4"}>
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-semibold">Productos</h1>
          {canCreateProduct && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded shadow hover:bg-blue-600 text-sm"
            >
              + Nuevo producto
            </button>
          )}
        </div>
        {/* Product identity search */}
        <div className="sticky top-0 pt-2 bg-white z-10 border-b mt-4 pb-2">
          <ProductSearchInput
            type="text"
            placeholder="Buscar producto..."
            className="border border-gray-300 rounded p-2 mb-2 w-full"
            value={search}
            products={products}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </div>

        <CategoryFilterBar
          categories={categoriesForDisplay}
          selectedCategory={selectedCategory}
          onSelect={setSelectedCategory}
          includeAll
          allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
          loading={categoriesLoading}
          loadingMessage={<InlineLoading label="Cargando categorías" />}
          error={categoriesError}
          emptyMessage="No hay categorías disponibles."
          isEmpty={categoriesForDisplay.length === 0}
          itemsWrapperClassName="mb-6"
        />

	        {/* Search Results Category */}
	        {search && searchVarietyResults.length > 0 && (
	          <div className="space-y-4 mb-6">
	            <div className="flex items-center gap-4 my-6">
	              <div className="flex-grow border-t border-gray-300"></div>
	              <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
	                Resultados de Búsqueda
	              </h2>
	              <div className="flex-grow border-t border-gray-300"></div>
	            </div>

	            <ProductGrid>
	              {searchVarietyResults.map(({ product, variety }) => (
	                <PosProductCard
	                  key={`${product.id}-${variety.id}`}
                    productName={product.productName}
                    subtitle={variety.varietyName}
                    imageSrc={variety.varietyImage || product.productImage || 'https://placehold.co/200'}
	                  onClick={() => setSelectedProductId(product.id)}
	                />
	              ))}
	            </ProductGrid>
	          </div>
	        )}

	        {search && suggestedSearchVarietyResults.length > 0 && (
	          <div className="space-y-4 mb-6">
	            <div className="flex items-center gap-4 my-6">
	              <div className="flex-grow border-t border-gray-300"></div>
	              <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
	                Sugeridos
	              </h2>
	              <div className="flex-grow border-t border-gray-300"></div>
	            </div>

	            <ProductGrid>
	              {suggestedSearchVarietyResults.map(({ product, variety }) => (
	                <PosProductCard
	                  key={`${product.id}-${variety.id}`}
                    productName={product.productName}
                    subtitle={variety.varietyName}
                    imageSrc={variety.varietyImage || product.productImage || 'https://placehold.co/200'}
	                  onClick={() => setSelectedProductId(product.id)}
	                />
	              ))}
	            </ProductGrid>
	          </div>
	        )}

        {/* Filtered Prices */}
        <div className="space-y-8">
          {categoriesToRender.map((category) => {
            const productsInCategory = filteredProducts
              .filter((p) => resolveProductCategoryId(p) === category.id)
              .sort((a, b) => a.productName.localeCompare(b.productName)); // Alphabetical

            if (productsInCategory.length === 0) return null;

            return (
              <div key={category.id}>
                {/* Category Title */}
                <div className="flex items-center gap-4 my-6">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                    {category.name}
                  </h2>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>

                {/* Products Grid */}
                <ProductGrid>
                  {productsInCategory.map((product) => (
                    <PosProductCard
                      key={product.id}
                      productName={product.productName}
                      imageSrc={product.productImage || 'https://placehold.co/200'}
                      onClick={() => setSelectedProductId(product.id)}
                    />
                  ))}
                </ProductGrid>
              </div>
            );
          })}
        </div>

        {!loading && products.length > 0 && (
          <div className="text-sm text-gray-400 text-center mt-4">
            Ya se cargaron todos los productos.
          </div>
        )}
        {/* Modal for product details and varieties. Editing replaces this dialog. */}
        {selectedProduct && !showEdit && !showAddVarietyModal && (
          <div
            className="fixed inset-0 z-[1500] flex items-stretch justify-center bg-slate-950/60 p-0 sm:items-center sm:p-4"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeSelectedProduct();
            }}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="product-detail-title"
              className="flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-3xl sm:rounded-2xl sm:border sm:border-gray-100"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <header className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3">
                  <img
                    src={selectedProduct.productImage || "https://placehold.co/160"}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded-xl border border-gray-200 object-cover"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Producto</p>
                    <h2 id="product-detail-title" className="truncate text-lg font-semibold text-gray-950">{selectedProduct.productName}</h2>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                      <span>
                        {resolveProductCategoryName(selectedProduct, categoriesForDisplay)}
                      </span>
                      <span>Códigos únicos en la empresa</span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeSelectedProduct}
                  aria-label="Cerrar detalle del producto"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                >
                  ✕
                </button>
              </header>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs text-gray-600">
                    {`Subcategoria: ${resolveProductSubcategoryName(selectedProduct, categoriesForDisplay)}`}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canUpdateProduct && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
                        onClick={() => setShowEdit(true)}
                        disabled={isLoading}
                      >
                        <FiEdit aria-hidden="true" /> Editar
                      </button>
                    )}
                    {canDeleteProduct && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteProduct(selectedProduct.id)}
                        disabled={isLoading}
                      >
                        <FiTrash2 aria-hidden="true" /> Eliminar
                      </button>
                    )}
                    {canUpdateProduct && (
                      <button
                        type="button"
                        className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                        onClick={() => openAddVariety()}
                      >
                        + Agregar tipo
                      </button>
                    )}
                  </div>
                </div>

                <VarietyGroupManager
                  branchId={branchId}
                  product={selectedProduct}
                  canUpdateProduct={canUpdateProduct}
                  activeGroupId={activeVarietyGroupId}
                  onCreateVariety={openAddVariety}
                />
                <VarietyGroupTabs
                  groups={selectedProduct?.VarietyGroups}
                  activeGroupId={activeVarietyGroupId}
                  onSelect={setActiveVarietyGroupId}
                  className="mb-4"
                />
                {activeVarietyGroup && canUpdateProduct && (
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-blue-100 bg-blue-50 p-3">
                    <span className="text-sm font-semibold text-gray-800">{activeVarietyGroup.name}</span>
                    <button type="button" onClick={() => openAddVariety()} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                      + Crear variedad en este grupo
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {selectedProductVarieties.map((variety) => (
                    <PresentationItem
                      key={variety.id}
                      branchId={branchId}
                      variety={variety}
                      fallbackImage={selectedProduct.productImage}
                      targetFormatId={targetFormatId}
                      onDeleteVariety={handleDeleteVariety}
                      onVarietySaved={handleVarietySaved}
                      canUpdateProduct={canUpdateProduct}
                      canManageFormats={hasPermission(user, 'stock.formats.manage')}
                      canReadPrices={canReadPrices}
                    />
                  ))}
                  {selectedProductVarieties.length === 0 && (
                    <p className="text-sm text-gray-500">No hay variedades para este grupo.</p>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductList;
